import React, { useState, useEffect, useMemo, useId } from "react";
import {
  Form,
  Row,
  Col,
  OverlayTrigger,
  Tooltip,
  Button,
  Spinner,
  Badge,
} from "react-bootstrap";
import ObjectConfigApi from "../../api/ObjectConfigApi";
import LookupLayout from "../layout/LookupLayout";
import moment from "moment";

export default function WorkflowCreateTask({
  config,
  onChange,
  onConfigPatch,
  objectName,
  readOnly,
}) {
  const [taskFields, setTaskFields] = useState({ main: [], description: null });
  const [picklistValues, setPicklistValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allFields, setAllFields] = useState([]);
  const [pickUserLookup, setPickUserLookup] = useState("");
  const [lookupRemountKey, setLookupRemountKey] = useState(0);
  const idPrefix = useId();

  // Fields to display in workflow task creation (description will be rendered last)
  const workflowFields = [
    "title",
    "ownerid",
    "priority",
    "status",
    "type",
  ];

  // Fetch task fields and picklist values
  useEffect(() => {
    fetchTaskFields();
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!objectName) {
        setAllFields([]);
        return;
      }
      try {
        const response = await ObjectConfigApi.fetchConfigObjectByNameWithFields(
          objectName
        );
        if (response.success && response.data?.fields) {
          setAllFields(response.data.fields);
        } else {
          setAllFields([]);
        }
      } catch (e) {
        console.error("WorkflowCreateTask: failed to load object fields", e);
        setAllFields([]);
      }
    };
    load();
  }, [objectName]);

  const userLookupFields = useMemo(() => {
    if (!allFields.length) return [];
    const out = [];
    for (const f of allFields) {
      const dt = (f.data_type || "").toLowerCase();
      const ref = (f.reference_object || "").toLowerCase();
      const isUserRef =
        ref === "user" && (dt === "lookup" || f.is_reference === true);
      if (isUserRef) {
        out.push(f);
      }
    }
    return out.sort((a, b) => {
      if (a.field_name === "ownerid") return -1;
      if (b.field_name === "ownerid") return 1;
      return String(a.field_label || a.field_name).localeCompare(
        String(b.field_label || b.field_name)
      );
    });
  }, [allFields]);

  const hasOwnerInUserLookups = useMemo(
    () => userLookupFields.some((f) => f.field_name === "ownerid"),
    [userLookupFields]
  );

  const userLookupLabel = useMemo(() => {
    const m = new Map();
    m.set("ownerid", "Record owner (ownerid)");
    for (const f of userLookupFields) {
      m.set(f.field_name, f.field_label || f.field_name);
    }
    return m;
  }, [userLookupFields]);

  const patchOwner = (partial) => {
    if (onConfigPatch) {
      onConfigPatch(partial);
    } else if (partial && typeof partial === "object") {
      for (const [k, v] of Object.entries(partial)) {
        onChange(k, v);
      }
    }
  };

  const assignHardcodedUser = (userId, displayName) => {
    if (!userId || String(userId).trim() === "") return;
    patchOwner({
      ownerid: String(userId).trim(),
      ownerName: displayName || "",
    });
    setLookupRemountKey((k) => k + 1);
  };

  const assignUserLookupField = (fieldName) => {
    const fn = String(fieldName).trim();
    if (!fn) return;
    patchOwner({
      ownerid: `{{${fn}}}`,
      ownerName: "",
    });
    setPickUserLookup("");
  };

  const clearOwner = () => {
    patchOwner({ ownerid: "", ownerName: "" });
    setLookupRemountKey((k) => k + 1);
  };

  const getUserLookupFieldFromOwnerId = () => {
    if (!config?.ownerid || typeof config.ownerid !== "string") return null;
    if (!config.ownerid.includes("{{")) return null;
    const m = config.ownerid.match(/\{\{\s*([^}]+?)\s*\}\}/);
    return m ? m[1].trim() : null;
  };

  const fetchTaskFields = async () => {
    try {
      setLoading(true);
      const response = await ObjectConfigApi.fetchConfigObjectByNameWithFields("task");

      console.log("Task fields response:", response);

      if (response.success && response.data) {
        const allFields = response.data.fields || [];

        // Filter to show only workflow-relevant fields (including description for later)
        const filteredFields = allFields.filter(field =>
          workflowFields.includes(field.field_name) || field.field_name === "description"
        );

        // Sort fields in the order we want to display them (excluding description)
        const sortedFields = workflowFields
          .map(fieldName => filteredFields.find(f => f.field_name === fieldName))
          .filter(Boolean); // Remove undefined entries

        // Store all fields including description separately
        const fieldsWithDescription = {
          main: sortedFields,
          description: filteredFields.find(f => f.field_name === "description")
        };

        console.log("Filtered task fields:", fieldsWithDescription);
        setTaskFields(fieldsWithDescription);

        // Extract picklist values - prefer picklist_values_data (active values from backend)
        // or filter picklist_values by is_active
        const picklistValuesMap = {};
        filteredFields.forEach(field => {
          if (field.data_type === "picklist" || field.data_type === "multipicklist") {
            let values = null;
            let needsFiltering = false;

            // First try picklist_values_data (new optimized field with only active values from backend)
            if (field.picklist_values_data && Array.isArray(field.picklist_values_data)) {
              values = field.picklist_values_data;
              // picklist_values_data is already filtered by backend (WHERE pv.is_active = true)
              needsFiltering = false;
            }
            // Fallback to picklist_values (may include inactive values)
            else if (field.picklist_values) {
              if (Array.isArray(field.picklist_values)) {
                values = field.picklist_values;
                needsFiltering = true; // Need to filter by is_active
              } else if (typeof field.picklist_values === "string") {
                try {
                  values = JSON.parse(field.picklist_values);
                  needsFiltering = true; // Need to filter by is_active
                } catch (e) {
                  console.error(`Failed to parse picklist values for ${field.field_name}:`, e);
                  values = null;
                }
              }
            }

            // Filter to only include active values if needed
            if (values && Array.isArray(values)) {
              let activeValues = values;

              if (needsFiltering) {
                // Filter to only include active values
                activeValues = values.filter(val => {
                  // If is_active property exists, only include active values
                  if (val && typeof val === 'object' && 'is_active' in val) {
                    return val.is_active === true;
                  }
                  // If is_active doesn't exist, include all (for backward compatibility)
                  return true;
                });
              }

              if (activeValues.length > 0) {
                picklistValuesMap[field.field_name] = activeValues;
              }
            }
          }
        });

        console.log("Picklist values:", picklistValuesMap);
        setPicklistValues(picklistValuesMap);
      }
    } catch (err) {
      console.error("Error fetching task fields:", err);
      setError("Failed to load task fields");
    } finally {
      setLoading(false);
    }
  };

  // Render field based on data type
  const renderField = (field) => {
    const fieldName = field.field_name;
    const fieldLabel = field.field_label;
    const dataType = field.data_type;
    const isRequired = field.is_required;
    const value = config?.[fieldName] || "";

    // Special handling for ownerid field
    if (fieldName === "ownerid") {
      const lookupTokenField = getUserLookupFieldFromOwnerId();
      const hasHardcodedUser =
        value &&
        !String(value).includes("{{") &&
        String(value).trim() !== "";

      return (
        <Col lg={12} key={fieldName}>
          <Form.Group>
            <Form.Label className="fw-semibold">
              {fieldLabel}
              {isRequired && !readOnly && (
                <span className="text-danger ms-1">*</span>
              )}
              <OverlayTrigger
                placement="right"
                overlay={
                  <Tooltip>
                    Same as Send Email: add a fixed user from directory, or assign
                    from a user lookup on this record (including record owner).
                  </Tooltip>
                }
              >
                <i className="fa fa-info-circle ms-2 text-muted" />
              </OverlayTrigger>
            </Form.Label>
            <div className="border rounded p-3 bg-light text-start">
              {!readOnly && (
                <div className="d-flex flex-wrap align-items-end gap-3 w-100 justify-content-start mb-3">
                  <div
                    style={{
                      minWidth: "220px",
                      maxWidth: "100%",
                      flex: "1 1 220px",
                    }}
                  >
                    <Form.Label className="small text-secondary mb-1 d-block">
                      Add user
                    </Form.Label>
                    <LookupLayout
                      key={lookupRemountKey}
                      objectName="user"
                      searchFields="email,firstname,lastname"
                      displayFields="firstname,lastname"
                      value=""
                      defaultLabel=""
                      onChange={(userId, userName) =>
                        assignHardcodedUser(userId, userName)
                      }
                      disabled={readOnly}
                      placeholder="Search to assign a user…"
                    />
                  </div>
                  <div style={{ minWidth: "220px", maxWidth: "100%" }}>
                    <Form.Label className="small text-secondary mb-1 d-block">
                      User lookup field
                    </Form.Label>
                    <Form.Select
                      id={`${idPrefix}-user-lookup`}
                      size="sm"
                      value={pickUserLookup}
                      onChange={(e) => {
                        const v = e.target.value;
                        setPickUserLookup(v);
                        if (v) assignUserLookupField(v);
                      }}
                      disabled={!objectName || readOnly}
                    >
                      <option value="">
                        {objectName
                          ? "Choose user lookup…"
                          : "Select workflow object first"}
                      </option>
                      {!hasOwnerInUserLookups && (
                        <option value="ownerid">Record owner (ownerid)</option>
                      )}
                      {userLookupFields.map((f) => (
                        <option key={f.field_name} value={f.field_name}>
                          {f.field_name === "ownerid"
                            ? "Record owner (ownerid)"
                            : f.field_label || f.field_name}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                </div>
              )}
              <div className={readOnly ? "" : "pt-2 border-top border-light"}>
                <div className="small text-secondary mb-2">Assigned owner</div>
                <div className="text-start w-100" style={{ lineHeight: 1.8 }}>
                  {hasHardcodedUser && (
                    <span className="d-inline-block me-2 mb-1 align-middle">
                      <Badge
                        bg="secondary"
                        className="d-flex align-items-center gap-1 py-2 px-2"
                      >
                        <span>
                          <i className="fa fa-user me-1" />
                          {config?.ownerName?.trim()
                            ? config.ownerName
                            : `${String(value).slice(0, 8)}…`}
                        </span>
                        {!readOnly && (
                          <Button
                            variant="link"
                            className="text-white p-0 ms-1 lh-1"
                            size="sm"
                            onClick={clearOwner}
                            aria-label="Remove"
                          >
                            &times;
                          </Button>
                        )}
                      </Badge>
                    </span>
                  )}
                  {lookupTokenField && (
                    <span className="d-inline-block me-2 mb-1 align-middle">
                      <Badge
                        bg="primary"
                        className="d-flex align-items-center gap-1 py-2 px-2"
                      >
                        <span>
                          <i className="fa fa-link me-1" />
                          {userLookupLabel.get(lookupTokenField) ||
                            lookupTokenField}
                        </span>
                        {!readOnly && (
                          <Button
                            variant="link"
                            className="text-white p-0 ms-1 lh-1"
                            size="sm"
                            onClick={clearOwner}
                            aria-label="Remove"
                          >
                            &times;
                          </Button>
                        )}
                      </Badge>
                    </span>
                  )}
                  {!hasHardcodedUser && !lookupTokenField && (
                    <span className="text-muted small d-block">
                      {readOnly
                        ? "No owner assigned."
                        : "Add a user or choose a user lookup field above."}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </Form.Group>
        </Col>
      );
    }

    // Handle different data types
    switch (dataType) {
      case "picklist":
        const options = picklistValues[fieldName] || [];
        return (
          <Col lg={6} key={fieldName}>
            <Form.Group>
              <Form.Label>{fieldLabel}</Form.Label>
              <Form.Select
                value={value}
                onChange={(e) => onChange(fieldName, e.target.value)}
                required={isRequired && !readOnly}
                disabled={readOnly}
              >
                <option value="">-Select {fieldLabel}-</option>
                {options.map((opt) => {
                  const optionKey = opt.key || opt.value_key;
                  const optionValue = opt.value || opt.value_label;
                  return (
                    <option key={optionKey} value={optionKey}>
                      {optionValue}
                    </option>
                  );
                })}
              </Form.Select>
            </Form.Group>
          </Col>
        );

      case "textarea":
      case "text":
        const colSize = dataType === "textarea" ? 12 : 6;
        return (
          <Col lg={colSize} key={fieldName}>
            <Form.Group>
              <Form.Label>{fieldLabel}</Form.Label>
              <Form.Control
                as={dataType === "textarea" ? "textarea" : "input"}
                type={dataType === "textarea" ? undefined : "text"}
                rows={dataType === "textarea" ? 2 : undefined}
                value={value}
                onChange={(e) => onChange(fieldName, e.target.value)}
                required={isRequired && !readOnly}
                disabled={readOnly}
              />
            </Form.Group>
          </Col>
        );

      default:
        return (
          <Col lg={6} key={fieldName}>
            <Form.Group>
              <Form.Label>{fieldLabel}</Form.Label>
              <Form.Control
                type="text"
                value={value}
                onChange={(e) => onChange(fieldName, e.target.value)}
                required={isRequired && !readOnly}
                disabled={readOnly}
              />
            </Form.Group>
          </Col>
        );
    }
  };

  if (loading) {
    return (
      <div className="text-center p-3">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading task fields...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger">
        {error}
      </div>
    );
  }

  return (
    <>
      <Row>
        {/* Render main dynamic task fields (title, ownerid, priority, status, type) */}
        {taskFields.main && taskFields.main.map((field) => renderField(field))}

        {/* Custom field: Complete in (Days) - appears after Type */}
        <Col lg={6}>
          <Form.Group>
            <Form.Label>
              Complete in (Days)
              <OverlayTrigger
                placement="right"
                overlay={
                  <Tooltip>
                    Number of days from task creation to completion. This will
                    automatically calculate the due date when the task is created.
                  </Tooltip>
                }
              >
                <i className="fa fa-info-circle mx-2 text-muted"></i>
              </OverlayTrigger>
            </Form.Label>
            <Form.Control
              type="number"
              min="1"
              value={config?.daysToComplete || ""}
              onChange={(e) =>
                onChange("daysToComplete", parseInt(e.target.value, 10) || "")
              }
              disabled={readOnly}
              placeholder="Enter number of days"
            />
            {config?.daysToComplete && (
              <Form.Text className="text-muted">
                Due date will be:{" "}
                {moment()
                  .add(parseInt(config.daysToComplete, 10), "days")
                  .format("MMM DD, YYYY")}
              </Form.Text>
            )}
          </Form.Group>
        </Col>

        {/* Description field - rendered last */}
        {taskFields.description && renderField(taskFields.description)}
      </Row>
    </>
  );
}
